import {
	AfterViewInit,
	Component,
	computed,
	DestroyRef,
	inject,
	OnDestroy,
	OnInit,
	signal,
	ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AvatarComponent,
	BaseComponent,
	SidenavComponent,
	TopnavComponent,
	EmptyStateComponent,
	LinkComponent,
	MenuComponent,
	IMenu,
	ISidenav,
	AlertComponent,
	ITopnavMenu,
	ViewMediaComponent,
	ModalComponent,
	ButtonIconComponent,
	FormGroupComponent,
	ToggleComponent,
	IToogleSelect,
	TileComponent,
	ProgressComponent,
} from '@tecalisdev/infinity';
import { LoadingService } from '../../../shared/services/loading.service';
import { HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { IMasterResponse } from '../../../shared/models/master.models';
import { MasterService } from '../../../shared/services/master.service';
import { NgIf } from '@angular/common';
import { ErrorService } from '../../services/error.service';
import { IUserDataResponse } from '../../../shared/models/user.models';
import { AuthService } from '../../services/auth.service';
import { OPERATION_TYPE } from '../../../operations/models/operations.models';
import { ViewMediaService } from '../../../shared/services/view-media.service';
import { RolePermissionsService } from '../../services/role-permissions.service';
import { environment } from '../../../../environments/environment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { tablet } from '../../utils/utils';
import { LanguageService } from '../../services/language.service';
import { Permission } from '../../models/role-permissions.models';
import { buildCurlGet } from '../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../shared/components/modal-curl-request/modal-curl-request.component';
import { PanelService } from '../../../panel/services/panel.service';
import { IOperationBalanceView } from '../../../panel/models/panel.models';
import {
	contractedItemsFromSnapshot,
	sumOperationBalanceItems,
} from '../../utils/operation-balance.util';
import { OperationBalanceModalComponent } from '../operation-balance-modal/operation-balance-modal.component';

@Component({
	selector: 'app-layout',
	imports: [
		BaseComponent,
		TopnavComponent,
		SidenavComponent,
		RouterOutlet,
		TranslatePipe,
		AvatarComponent,
		EmptyStateComponent,
		NgIf,
		LinkComponent,
		MenuComponent,
		ViewMediaComponent,
		ModalComponent,
		AlertComponent,
		ButtonIconComponent,
		ModalCurlRequestComponent,
		FormGroupComponent,
		ToggleComponent,
		FormsModule,
		TileComponent,
		ProgressComponent,
		OperationBalanceModalComponent,
	],
	templateUrl: './layout.component.html',
	styleUrl: './layout.component.scss',
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
	public viewMedia = inject(ViewMediaService);
	public errorService = inject(ErrorService);
	private loading = inject(LoadingService);
	private masterService = inject(MasterService);
	private trans = inject(TranslateService);
	private authService = inject(AuthService);
	private breakpointObserver = inject(BreakpointObserver);
	public rolePermissions = inject(RolePermissionsService);
	private panelService = inject(PanelService);
	private destroyRef = inject(DestroyRef);

	private languageService = inject(LanguageService);

	/** NIF-775: saldo prepago en sidenav; null = oculto. */
	public readonly operationBalance = signal<IOperationBalanceView | null>(null);
	public readonly operationBalanceModalOpen = signal(false);

	@ViewChild(TopnavComponent) private topnav?: TopnavComponent;

	public user: IUserDataResponse;
	public operationType = OPERATION_TYPE;
	public legacyAdminUrl = environment?.legacyAdmin;
	public isProduction = environment.production;
	public tabletSize = signal<boolean>(false);

	public readonly languageModalOpen = signal(false);
	/** Borrador del idioma en el modal (no se aplica hasta Guardar). */
	public selectedLang: IToogleSelect | null = null;
	public readonly languageToggleOptions: IToogleSelect[] = [
		{ id: 'es', label: 'Español' },
		{ id: 'en', label: 'English' },
		{ id: 'pt', label: 'Portugués' },
	];

	public readonly viewMediaCurlOpen = signal(false);
	public readonly viewMediaCurlTitleKey = signal('PROCESSES.CURL_MODAL.TITLE_MEDIA');
	public readonly viewMediaCurlText = signal('');

	public canViewDeveloperCurl(): boolean {
		return this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION);
	}

	public openViewMediaCurl(): void {
		const curl = this.viewMedia.viewMedia()?.developerCurl;
		if (!curl?.path) {
			return;
		}
		this.viewMediaCurlTitleKey.set(curl.titleKey);
		this.viewMediaCurlText.set(buildCurlGet(environment.apiUrl, curl.path));
		this.viewMediaCurlOpen.set(true);
	}

	public closeViewMediaCurl(): void {
		this.viewMediaCurlOpen.set(false);
	}

	public tabletMenu = computed<ITopnavMenu[] | undefined>(() => {
		if (!this.tabletSize()) return undefined;
		return this.sidenavConfig().map((item) => {
			if (item.menu) {
				return {
					label: item.label,
					icon: item.icon,
					menu: item.menu.map((sub) => ({ label: sub.label, url: sub.url })),
				};
			}
			return { label: item.label, icon: item.icon, url: item.url };
		});
	});

	/**
	 * Configuración del sidenav basada en permisos
	 */
	public sidenavConfig = computed(() => {
		this.languageService.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const baseConfig: ISidenav[] = [
			// PANEL
			{ icon: 'icon-home', label: this.trans.instant('SIDENAV.PANEL'), url: '/panel' },
		];

		// PROCESSES
		if (this.rolePermissions.canViewProcesses()) {
			baseConfig.push({
				icon: 'icon-list',
				label: this.trans.instant('SIDENAV.PROCESSES'),
				url: '/processes',
			});
		}

		// OPERATIONS
		if (this.rolePermissions.canViewOperations()) {
			baseConfig.push({
				icon: 'icon-pending-check',
				label: this.trans.instant('SIDENAV.OPERATIONS'),
				menu: [
					{
						label: this.trans.instant('SIDENAV.FORMS'),
						url: '/operations/' + OPERATION_TYPE.FORM.toLowerCase(),
					},
					{
						label: this.trans.instant('SIDENAV.ID_DOCUMENT'),
						url: '/operations/' + OPERATION_TYPE.OCR.toLowerCase(),
					},
					{
						label: this.trans.instant('SIDENAV.POL'),
						url: '/operations/' + OPERATION_TYPE.POL.toLowerCase(),
					},
					{
						label: this.trans.instant('SIDENAV.DOCUMENTS'),
						url: '/operations/' + OPERATION_TYPE.DOCUMENTS.toLowerCase(),
					},
					{
						label: this.trans.instant('SIDENAV.FINANCIAL_EVALUATION'),
						url: '/operations/' + OPERATION_TYPE.FINANCIAL_EVALUATION.toLowerCase(),
					},
					{
						label: this.trans.instant('SIDENAV.OPERATIONS_MESSAGING'),
						url: '/operations/' + OPERATION_TYPE.MESSAGING.toLowerCase(),
					},
					{
						divider: true,
						label: this.trans.instant('SIDENAV.CONNECTIONS'),
						url: '/operations/' + OPERATION_TYPE.CONNECTIONS.toLowerCase(),
					},
				],
			});
		}

		// WORKFLOWS
		if (this.rolePermissions.canViewWorkflows()) {
			baseConfig.push({
				icon: 'icon-workflow',
				label: this.trans.instant('SIDENAV.WORKFLOWS'),
				url: '/workflows',
			});
		}

		// IDENTITIES
		if (this.rolePermissions.canViewPersons()) {
			baseConfig.push({
				icon: 'icon-users',
				label: this.trans.instant('SIDENAV.IDENTITIES'),
				url: '/identities',
			});
		}

		// REPORTS
		if (this.rolePermissions.canViewReports()) {
			baseConfig.push({
				icon: 'icon-download-report',
				label: this.trans.instant('SIDENAV.REPORTS'),
				url: '/reports',
			});
		}

		if (this.rolePermissions.canViewUserActivity()) {
			baseConfig.push({
				icon: 'icon-pipeline',
				label: this.trans.instant('SIDENAV.ACTIVITY'),
				url: '/activity',
			});
		}

		// DEVELOPERS (NIF-764)
		if (this.rolePermissions.canViewDevelopers()) {
			baseConfig.push({
				icon: 'icon-database',
				label: this.trans.instant('SIDENAV.DEVELOPERS'),
				url: '/developers',
				divider: true,
			});
			// baseConfig.push({
			// 	icon: 'icon-play',
			// 	label: this.trans.instant('SIDENAV.TESTING_CENTER'),
			// 	url: '/developers/testing-center',
			// });
		}

		// SETTINGS
		if (this.rolePermissions.canViewSettings()) {
			const settingsMenu: ISidenav[] = [
				{ icon: 'icon-user', label: this.trans.instant('SIDENAV.USER'), url: '/settings/users' },
				{
					icon: 'icon-briefcase',
					label: this.trans.instant('SIDENAV.COMPANIES'),
					url: '/settings/companies',
				},
				{
					icon: 'icon-customize',
					label: this.trans.instant('SIDENAV.DESIGN'),
					url: '/settings/design',
					divider: true,
				},
				{
					icon: 'icon-check-circle',
					label: this.trans.instant('SIDENAV.LEGAL_TERMS'),
					url: '/settings/legal-terms',
				},
			];

			if (this.rolePermissions.canViewErrorCatalog()) {
				settingsMenu.push({
					icon: 'icon-alert-triangle',
					label: this.trans.instant('SIDENAV.ERROR_CATALOG'),
					url: '/settings/error-catalog',
				});
			}

			if (this.rolePermissions.canViewClientAlerts()) {
				settingsMenu.push({
					icon: 'icon-bell',
					label: this.trans.instant('SIDENAV.CLIENT_ALERTS'),
					url: '/settings/client-alerts',
				});
			}

			baseConfig.push({
				icon: 'icon-settings',
				label: this.trans.instant('SIDENAV.SETTING'),
				menu: settingsMenu,
			});
		}

		return baseConfig;
	});

	constructor() {
		this.user = this.masterService.user();
	}

	ngOnInit(): void {
		this.breakpointObserver.observe([tablet]).subscribe((x) => {
			this.tabletSize.set(x.breakpoints[tablet]);
			if (!x.breakpoints[tablet]) {
				document.querySelector('app-root')?.classList.remove('topnav-open');
			}
		});
		this.loadOperationBalance();
	}

	/** NIF-775: Sanctum → GET /company/{id}/consumptions; fallo / no prepago → ocultar. */
	private loadOperationBalance(): void {
		const userData = this.masterService.user()?.data;
		const companyId = Number(userData?.company_id ?? 0);
		if (
			!userData?.company_prepaid ||
			userData?.company_unlimited ||
			!Number.isFinite(companyId) ||
			companyId <= 0
		) {
			this.operationBalance.set(null);
			return;
		}

		this.panelService
			.getCompanyConsumptions(companyId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (snapshot) => {
					if (!snapshot || snapshot.unlimited === true) {
						this.operationBalance.set(null);
						return;
					}
					const items = contractedItemsFromSnapshot(snapshot);
					const totals = sumOperationBalanceItems(items);
					if (!totals) {
						this.operationBalance.set(null);
						return;
					}
					const packName = snapshot.pack_name;
					this.operationBalance.set({
						...totals,
						packName: typeof packName === 'string' && packName !== '' ? packName : null,
						items,
					});
				},
				error: () => {
					this.operationBalance.set(null);
				},
			});
	}

	public openOperationBalanceModal(): void {
		if (!this.operationBalance()) {
			return;
		}
		this.operationBalanceModalOpen.set(true);
	}

	public closeOperationBalanceModal(): void {
		this.operationBalanceModalOpen.set(false);
	}

	ngAfterViewInit(): void {
		const topnav = this.topnav;
		if (!topnav) {
			return;
		}
		const originalCloseAll = topnav.closeAll.bind(topnav);
		topnav.closeAll = () => {
			originalCloseAll();
			this.applyTopnavScrollLock(topnav.isOpen);
		};
	}

	ngOnDestroy(): void {
		document.querySelector('app-root')?.classList.remove('topnav-open');
	}

	/** Misma lógica que `hiddenScroll()` del topnav de Infinity (solo faltaba en `closeAll`). */
	private applyTopnavScrollLock(isOpen: boolean): void {
		const root = document.querySelector('app-root');
		if (!root) {
			return;
		}
		if (isOpen) {
			root.classList.add('topnav-open');
		} else {
			root.classList.remove('topnav-open');
		}
	}

	private fetchData(): Observable<any> {
		const subs: any = {
			master: this.masterService.getMasterFromService(),
			user: this.masterService.getUserFromService(),
		};
		if (this.masterService.user()) {
			delete subs.user;
			this.user = this.masterService.user();
		}
		if (this.masterService.master()) {
			delete subs.master;
		}
		return forkJoin(subs);
	}

	public getMasterData(): void {
		this.loading.show(this.trans.instant('LOADING.INIT'));
		this.errorService.hideGeneralError();
		this.fetchData().subscribe({
			next: (response: { master: { data: IMasterResponse }; user: IUserDataResponse }) => {
				this.masterService.setMaster({
					...response.master.data,
				});
				if (response.user) {
					this.user = response.user;
					this.masterService.setUser(response.user);
				}
			},
			error: (error: HttpErrorResponse) => {
				this.errorService.showGeneralError(() => this.getMasterData());
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	public menuChange(action: IMenu): void {
		const actions: Record<string, () => void> = {
			language: () => this.openLanguageModal(),
			tecalisAccount: () => window.open(`${environment.accountUrl}/home`, '_blank'),
			logout: () => this.authService.logout(),
		};

		actions[action.id]?.();
	}

	public openLanguageModal(): void {
		const current = this.languageService.getLanguage();
		this.selectedLang =
			this.languageToggleOptions.find((opt) => opt.id === current) ?? this.languageToggleOptions[0];
		this.languageModalOpen.set(true);
	}

	public closeLanguageModal(type: string): void {
		if (type === 'buttonPrimary') {
			const langId = this.selectedLang?.id;
			if (typeof langId === 'string' && langId) {
				this.languageService.setLanguage(langId);
			}
		}
		this.languageModalOpen.set(false);
	}

	private getTranslatedMenuLabel(key: string, fallbackEs: string, fallbackEn: string): string {
		const translated = this.trans.instant(key);
		if (translated !== key) {
			return translated;
		}

		return this.languageService.currentLang() === 'en' ? fallbackEn : fallbackEs;
	}
}
